/**
 * AccountGate — what an authenticated but not-yet-usable account sees.
 *
 * Two cases: `pending` (waiting for an admin to approve) and `suspended`
 * (access withdrawn). Both are dead ends by design, with a way out only via
 * logout, so nobody sits on a broken workspace wondering what went wrong.
 */
import { Clock, Ban, LogOut, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export function AccountGate({ status }) {
  const { user, logout, refresh } = useAuth();
  const suspended = status === "suspended";

  const Icon = suspended ? Ban : Clock;
  const tone = suspended
    ? "text-danger bg-danger-solid/10 border-danger/30"
    : "text-warning bg-warning-solid/10 border-warning/30";

  return (
    <div
      data-testid={suspended ? "account-suspended" : "account-pending"}
      className="min-h-screen flex items-center justify-center bg-surface-subtle px-4"
    >
      <div className="w-full max-w-md bg-surface border border-line rounded-2xl p-6 space-y-5">
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs ${tone}`}>
          <Icon className="w-3.5 h-3.5" />
          {suspended ? "Accesso sospeso" : "In attesa di approvazione"}
        </div>

        <div>
          <h1 className="text-xl font-semibold text-content tracking-tight">
            {suspended
              ? "Il tuo accesso è stato sospeso"
              : "Ci siamo quasi"}
          </h1>
          <p className="text-sm text-content-secondary mt-2 leading-relaxed">
            {suspended
              ? "Contatta l'amministratore per riattivare il tuo account. I tuoi dati sono conservati e li ritroverai al rientro."
              : "Il tuo account è stato creato e attende l'approvazione dell'amministratore. Appena viene abilitato potrai analizzare i tuoi ETF e salvare i portafogli."}
          </p>
        </div>

        <div className="text-xs text-content-muted bg-surface-subtle border border-line rounded-lg px-3 py-2.5">
          Account: <span className="text-content-secondary">{user?.email}</span>
        </div>

        <div className="flex items-center gap-2">
          {!suspended && (
            <button
              data-testid="account-gate-refresh"
              onClick={refresh}
              className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-brand hover:bg-brand-hover text-content-on-brand text-sm font-medium transition-colors duration-base ease-system"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Controlla di nuovo
            </button>
          )}
          <button
            data-testid="account-gate-logout"
            onClick={logout}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-line text-content-secondary hover:text-content hover:border-line-strong text-sm transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Esci
          </button>
        </div>
      </div>
    </div>
  );
}

export default AccountGate;
